import { useState, useRef } from 'react';
import { Plus, UserPlus, MapPin, CheckCircle, Video, Users, Youtube, ExternalLink, Copy, Check, Upload, Download, FileText, History, AlertTriangle, Bell } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { parseCSV, generateAttendanceCSV, downloadCSV, generateAttendancePDF, downloadTextAsFile } from '@/utils/csv';
import { notificationService } from '@/utils/notifications';

// --- CHAPTER DATA ---
const CLASS_11_CHAPTERS = [
  "Some Basic Concepts of Chemistry",
  "Structure of Atom",
  "Classification of Elements",
  "Chemical Bonding",
  "States of Matter",
  "Thermodynamics",
  "Equilibrium",
  "Redox Reactions",
  "Hydrogen",
  "s-Block Elements",
  "p-Block Elements",
  "Organic Chemistry Basics",
  "Hydrocarbons",
  "Environmental Chemistry"
];

const CLASS_12_CHAPTERS = [
  "Solutions",
  "Electrochemistry",
  "Chemical Kinetics",
  "Surface Chemistry",
  "General Principles and Processes of Isolation of Elements",
  "p-Block Elements",
  "d- and f-Block Elements",
  "Coordination Compounds",
  "Haloalkanes and Haloarenes",
  "Alcohols, Phenols and Ethers",
  "Aldehydes, Ketones and Carboxylic Acids",
  "Amines",
  "Biomolecules",
  "Polymers",
  "Chemistry in Everyday Life"
];

// Mock Database Types
type Student = {
  id: string;
  name: string;
  username: string;
  present: boolean;
  absentStreak: number;
  deviceId?: string;
};

type VideoResource = {
  id: string;
  classLevel: string;
  chapter: string;
  title: string;
  url: string;
  chapters?: { time: string; title: string }[];
  analytics?: { rewatched: number }[];
};

type OverrideLog = {
  id: string;
  studentId: string;
  studentName: string;
  action: 'marked_present' | 'marked_absent';
  reason: string;
  timestamp: Date;
};

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- STATE MANAGEMENT ---
  const [students, setStudents] = useState<Student[]>([
    { id: '1', name: 'Rahul Kumar', username: 'rahul123', present: false, absentStreak: 2 },
    { id: '2', name: 'Priya Singh', username: 'priya_s', present: true, absentStreak: 0 },
    { id: '3', name: 'Amit Sharma', username: 'amit_s', present: false, absentStreak: 3 },
    { id: '4', name: 'Sneha Patel', username: 'sneha_p', present: true, absentStreak: 0 },
  ]);

  const [videos, setVideos] = useState<VideoResource[]>([
    { id: '1', classLevel: '11', chapter: 'Structure of Atom', title: 'Bohr Model Explained', url: 'https://youtube.com/watch?v=abc123' },
    { id: '2', classLevel: '12', chapter: 'Solutions', title: "Raoult's Law", url: 'https://youtube.com/watch?v=def456' },
    { id: '3', classLevel: '11', chapter: 'Chemical Bonding', title: 'Ionic vs Covalent Bonds', url: 'https://youtube.com/watch?v=ghi789' },
  ]);

  const [overrideLogs, setOverrideLogs] = useState<OverrideLog[]>([
    { id: '1', studentId: '1', studentName: 'Rahul Kumar', action: 'marked_present', reason: 'Phone battery dead', timestamp: new Date(Date.now() - 86400000) }
  ]);

  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [showYouTubeDialog, setShowYouTubeDialog] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [overrideReason, setOverrideReason] = useState('');
  const [csvText, setCsvText] = useState('');
  const [uploadedUrl, setUploadedUrl] = useState('');

  // Forms State
  const [newStudentName, setNewStudentName] = useState('');
  const [videoForm, setVideoForm] = useState({
    classLevel: '11',
    chapter: '',
    title: '',
    description: ''
  });

  // Get chapters based on selected class
  const availableChapters = videoForm.classLevel === '11' ? CLASS_11_CHAPTERS : CLASS_12_CHAPTERS;

  // --- ACTIONS ---

  const handleConnectGoogle = () => {
    setIsGoogleConnected(true);
    notificationService.addNotification('Google Connected', 'YouTube integration is now active', 'success');
  };

  // Bulk Upload from CSV
  const handleBulkUpload = () => {
    const names = parseCSV(csvText);
    if (names.length === 0) {
      alert('No valid names found!');
      return;
    }

    const newStudents = names.map(name => ({
      id: Math.random().toString(),
      name,
      username: name.toLowerCase().replace(/\s/g, '') + Math.floor(Math.random() * 1000),
      present: false,
      absentStreak: 0
    }));

    setStudents([...students, ...newStudents]);
    setCsvText('');
    setShowBulkUpload(false);
    notificationService.addNotification('Students Added', `${newStudents.length} students imported successfully`, 'success');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvText(event.target?.result as string);
    };
    reader.readAsText(file);
  };

  // Export Attendance
  const handleExportCSV = () => {
    const csv = generateAttendanceCSV(students, new Date().toISOString().split('T')[0]);
    downloadCSV(csv, `attendance_${new Date().toISOString().split('T')[0]}.csv`);
    notificationService.addNotification('Report Exported', 'Attendance CSV downloaded', 'success');
  };

  const handleExportPDF = () => {
    const pdf = generateAttendancePDF(students, new Date().toISOString().split('T')[0]);
    downloadTextAsFile(pdf, `attendance_${new Date().toISOString().split('T')[0]}.txt`);
    notificationService.addNotification('Report Exported', 'Attendance report downloaded', 'success');
  };

  // Check for absent streaks and alert
  const checkAbsentStreaks = () => {
    const studentsWithStreak = students.filter(s => s.absentStreak >= 3);
    if (studentsWithStreak.length > 0) {
      studentsWithStreak.forEach(student => {
        notificationService.addNotification(
          'Absentee Alert',
          `${student.name} has been absent for ${student.absentStreak} consecutive days`,
          'warning'
        );
      });
    }
  };

  const handleOpenYouTubeUpload = () => {
    if (!videoForm.chapter || !videoForm.title) {
      alert('Please select class, chapter and enter video title first!');
      return;
    }

    const params = new URLSearchParams({
      title: `${videoForm.title} | Class ${videoForm.classLevel} Chemistry - ${videoForm.chapter}`,
      description: videoForm.description || `Learn ${videoForm.title} for Class ${videoForm.classLevel} Chemistry - ${videoForm.chapter}. \n\n#Chemistry #Class${videoForm.classLevel} #${videoForm.chapter.replace(/\s/g, '')}`,
      tags: `chemistry,class${videoForm.classLevel},${videoForm.chapter.toLowerCase().replace(/\s/g, ',')},education`
    });

    window.open(`https://studio.youtube.com/channel/upload?${params.toString()}`, '_blank');
    setShowYouTubeDialog(true);
  };

  const handleAddVideo = () => {
    if (!videoForm.chapter || !videoForm.title || !uploadedUrl) {
      alert('Please fill all fields and paste the YouTube link!');
      return;
    }
    const newVideo: VideoResource = {
      id: Math.random().toString(),
      classLevel: videoForm.classLevel,
      chapter: videoForm.chapter,
      title: videoForm.title,
      url: uploadedUrl,
      chapters: [
        { time: '00:00', title: 'Introduction' },
        { time: '02:30', title: 'Key Concepts' },
        { time: '05:20', title: `${videoForm.title} - Main Content` },
        { time: '10:45', title: 'Practice Problems' }
      ],
      analytics: [10, 25, 45, 30, 60, 20, 15].map(n => ({ rewatched: n }))
    };
    setVideos([...videos, newVideo]);
    setVideoForm({ classLevel: '11', chapter: '', title: '', description: '' });
    setUploadedUrl('');
    setShowYouTubeDialog(false);
    notificationService.addNotification('Video Added', `${videoForm.title} uploaded to library`, 'success');
  };

  const handleAddStudent = () => {
    if (!newStudentName) return;
    const newStudent: Student = {
      id: Math.random().toString(),
      name: newStudentName,
      username: newStudentName.toLowerCase().replace(/\s/g, '') + '123',
      present: false,
      absentStreak: 0
    };
    setStudents([...students, newStudent]);
    setNewStudentName('');
    notificationService.addNotification('Student Added', `${newStudent.name} registered`, 'success');
  };

  const openOverrideDialog = (studentId: string) => {
    setSelectedStudentId(studentId);
    setOverrideReason('');
    setShowOverrideDialog(true);
  };

  const handleManualOverride = () => {
    const student = students.find(s => s.id === selectedStudentId);
    if (!student || !overrideReason) return;

    const newStatus = !student.present;
    setStudents(students.map(s =>
      s.id === selectedStudentId
        ? { ...s, present: newStatus, absentStreak: newStatus ? 0 : s.absentStreak }
        : s
    ));

    const log: OverrideLog = {
      id: Math.random().toString(),
      studentId: selectedStudentId,
      studentName: student.name,
      action: newStatus ? 'marked_present' : 'marked_absent',
      reason: overrideReason,
      timestamp: new Date()
    };
    setOverrideLogs([log, ...overrideLogs]);
    setShowOverrideDialog(false);
    setOverrideReason('');
  };

  const presentCount = students.filter(s => s.present).length;
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              className="rounded-xl hover:bg-slate-100"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </Button>
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">Teacher Command Center</h1>
              <p className="text-xs text-slate-500">Manage students, attendance & content</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {!isGoogleConnected ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleConnectGoogle}
                className="gap-2"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Connect Google
              </Button>
            ) : (
              <Badge className="bg-green-500 gap-1">
                <CheckCircle className="h-3 w-3" />
                YouTube Connected
              </Badge>
            )}
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-slate-700">Attendance Today</p>
              <p className="text-xs text-slate-500">{presentCount}/{students.length} Present</p>
            </div>
            <Badge className="bg-green-500">{Math.round((presentCount / students.length) * 100)}%</Badge>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs defaultValue="attendance" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="attendance" className="gap-2">
              <CheckCircle className="h-4 w-4" />
              Attendance & Students
            </TabsTrigger>
            <TabsTrigger value="content" className="gap-2">
              <Youtube className="h-4 w-4" />
              Upload to YouTube
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="h-4 w-4" />
              Override Log
            </TabsTrigger>
          </TabsList>

          {/* --- TAB 1: ATTENDANCE & STUDENTS --- */}
          <TabsContent value="attendance" className="space-y-4">
            {/* Export Buttons */}
            <div className="flex gap-2 mb-4">
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPDF}>
                <FileText className="h-4 w-4 mr-2" />
                Export Report
              </Button>
              <Button variant="outline" size="sm" onClick={checkAbsentStreaks}>
                <Bell className="h-4 w-4 mr-2" />
                Check Absentees
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Add Student Section */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <UserPlus className="h-5 w-5 text-blue-500" />
                    Register Student
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    placeholder="Enter Student Name"
                    value={newStudentName}
                    onChange={(e) => setNewStudentName(e.target.value)}
                    className="h-12"
                  />
                  <Button
                    onClick={handleAddStudent}
                    className="w-full h-12"
                    disabled={!newStudentName}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Student
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-2 text-slate-500">or</span>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full h-12"
                    onClick={() => setShowBulkUpload(true)}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Bulk Upload (CSV)
                  </Button>
                </CardContent>
              </Card>

              {/* Manual Attendance Section */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MapPin className="h-5 w-5 text-orange-500" />
                    Live Attendance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {students.map((student) => (
                      <div
                        key={student.id}
                        className={`flex items-center justify-between p-4 rounded-xl border transition-all ${student.present
                          ? 'bg-green-50 border-green-200'
                          : 'bg-white border-slate-200'
                          }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${student.present
                            ? 'bg-green-500 text-white'
                            : 'bg-slate-200 text-slate-600'
                            }`}>
                            {student.name.charAt(0)}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{student.name}</p>
                            <p className="text-xs text-slate-500">@{student.username}</p>
                            {student.absentStreak >= 2 && (
                              <Badge variant="destructive" className="text-xs mt-1">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {student.absentStreak} days absent
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openOverrideDialog(student.id)}
                          >
                            Override
                          </Button>
                          <Button
                            variant={student.present ? "default" : "outline"}
                            className={student.present
                              ? "bg-green-600 hover:bg-green-700"
                              : "border-slate-300"
                            }
                            onClick={() => openOverrideDialog(student.id)}
                          >
                            {student.present ? (
                              <><CheckCircle className="h-4 w-4 mr-2" /> Present</>
                            ) : (
                              "Mark Present"
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* --- TAB 2: YOUTUBE UPLOAD --- */}
          <TabsContent value="content">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Youtube className="h-5 w-5 text-red-500" />
                    Upload to YouTube
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!isGoogleConnected && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
                      <p className="text-sm text-amber-800 mb-2">
                        Connect your Google account to upload videos directly to YouTube
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleConnectGoogle}
                        className="gap-2"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Connect Google Account
                      </Button>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <Select
                      value={videoForm.classLevel}
                      onValueChange={(val) => setVideoForm({ ...videoForm, classLevel: val, chapter: '' })}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select Class" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="11">Class 11 Chemistry</SelectItem>
                        <SelectItem value="12">Class 12 Chemistry</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select
                      value={videoForm.chapter}
                      onValueChange={(val) => setVideoForm({ ...videoForm, chapter: val })}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select Chapter" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {availableChapters.map((chapter) => (
                          <SelectItem key={chapter} value={chapter}>
                            {chapter}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Input
                    placeholder="Video Title (e.g., Bohr Model Explained)"
                    value={videoForm.title}
                    onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                    className="h-12"
                  />

                  <textarea
                    placeholder="Video Description (optional - will be auto-generated if empty)"
                    value={videoForm.description}
                    onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                    className="w-full min-h-[100px] px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />

                  <Button
                    className="w-full h-14 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl shadow-lg"
                    onClick={handleOpenYouTubeUpload}
                    disabled={!videoForm.chapter || !videoForm.title}
                  >
                    <ExternalLink className="h-5 w-5 mr-2" />
                    Open YouTube Studio & Upload
                  </Button>

                  <p className="text-xs text-slate-500 text-center">
                    This will open YouTube Studio in a new tab with pre-filled title and description
                  </p>
                </CardContent>
              </Card>

              {/* Existing Videos with Analytics */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Video Library ({videos.length} videos)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {videos.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No videos uploaded yet</p>
                    ) : (
                      videos.map((video) => (
                        <div key={video.id} className="p-4 bg-white border border-slate-200 rounded-xl hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={video.classLevel === '11' ? 'bg-blue-500' : 'bg-purple-500'}>
                                  Class {video.classLevel}
                                </Badge>
                                <span className="text-xs text-slate-500">{video.chapter}</span>
                              </div>
                              <p className="font-semibold text-slate-900">{video.title}</p>

                              {/* AI Chapters */}
                              {video.chapters && (
                                <div className="mt-2 space-y-1">
                                  <p className="text-xs font-medium text-slate-600">AI-Generated Chapters:</p>
                                  {video.chapters.map((ch, i) => (
                                    <div key={i} className="flex items-center gap-2 text-xs text-slate-500">
                                      <span className="font-mono bg-slate-100 px-1.5 rounded">{ch.time}</span>
                                      <span>{ch.title}</span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Analytics */}
                              {video.analytics && (
                                <div className="mt-2">
                                  <p className="text-xs font-medium text-slate-600 mb-1">Re-watch Heatmap (Hard Topics):</p>
                                  <div className="flex gap-1 h-6">
                                    {video.analytics.map((count, i) => (
                                      <div
                                        key={i}
                                        className={`flex-1 rounded-sm ${count.rewatched > 40 ? 'bg-red-500' : count.rewatched > 25 ? 'bg-orange-400' : 'bg-green-400'}`}
                                        title={`Segment ${i + 1}: ${count.rewatched} re-watches`}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="shrink-0"
                              onClick={() => copyToClipboard(video.url)}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* --- TAB 3: OVERRIDE LOG --- */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5 text-slate-500" />
                  Manual Override History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {overrideLogs.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No manual overrides yet</p>
                ) : (
                  <div className="space-y-3">
                    {overrideLogs.map((log) => (
                      <div key={log.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-slate-900">{log.studentName}</p>
                            <p className="text-sm text-slate-600">
                              {log.action === 'marked_present' ? '✓ Marked Present' : '✗ Marked Absent'}
                            </p>
                            <p className="text-sm text-slate-500 mt-1">
                              <span className="font-medium">Reason:</span> {log.reason}
                            </p>
                          </div>
                          <span className="text-xs text-slate-400">
                            {log.timestamp.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-500" />
              Bulk Upload Students
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Upload a CSV file or paste names (comma or newline separated)
            </p>

            <Input
              type="file"
              accept=".csv,.txt"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="h-12"
            />

            <textarea
              placeholder="Or paste names here...
Example:
Rahul Kumar
Priya Singh
Amit Sharma"
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              className="w-full min-h-[150px] px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowBulkUpload(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleBulkUpload}
                disabled={!csvText.trim()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import {parseCSV(csvText).length} Students
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Override Reason Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manual Override</DialogTitle>
            <DialogDescription>
              Please provide a reason for manually marking this student
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Input
              placeholder="Reason (e.g., Phone battery dead, Network issues)"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              className="h-12"
            />

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowOverrideDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleManualOverride}
                disabled={!overrideReason.trim()}
              >
                Save Override
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* YouTube Upload Dialog */}
      <Dialog open={showYouTubeDialog} onOpenChange={setShowYouTubeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Youtube className="h-5 w-5 text-red-500" />
              Paste YouTube Link
            </DialogTitle>
            <DialogDescription>
              After uploading your video on YouTube, paste the video link here to add it to the library.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700 mb-2">Video Details:</p>
              <p className="text-sm text-slate-600"><strong>Class:</strong> {videoForm.classLevel}</p>
              <p className="text-sm text-slate-600"><strong>Chapter:</strong> {videoForm.chapter}</p>
              <p className="text-sm text-slate-600"><strong>Title:</strong> {videoForm.title}</p>
            </div>

            <Input
              placeholder="Paste YouTube video URL here..."
              value={uploadedUrl}
              onChange={(e) => setUploadedUrl(e.target.value)}
              className="h-12"
            />

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowYouTubeDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-red-600 to-red-700"
                onClick={handleAddVideo}
                disabled={!uploadedUrl}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add to Library
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherDashboard;
